import { Languages } from "@app/types/multilingual";
import { StopStatus } from "@app/domain";
import { t, tPlural } from "@app/utils/translation";

export type ComputeTrackingStatusLabelInput = {
  isDropoffTracking: boolean;
  /** Raw backend status code — `OrderStatusInfo.code`. */
  statusCode: string;
  remainingQueuePosition: number | null;
  /** The tracked dropoff's own status — `null` outside dropoff-tracking
   * mode. Checked ahead of `statusCode`/`remainingQueuePosition`: the
   * order-level status can still read "PickedUp" (other dropoffs in the
   * driver's queue not yet done) after this specific dropoff has already
   * been delivered, so its own "done" state is the only reliable signal
   * that it's complete. */
  dropoffStatus: StopStatus | null;
  /** `order.status.label` — used whenever `isDropoffTracking` is false, and
   * for any status this rule doesn't override (Pending/Assigned/Done/...). */
  fallbackLabel: string;
  language: Languages;
};

/**
 * Pure port of the legacy `AddressBoxContainer`'s `pickupStatusText`/
 * `dropoffStatusText` derivation (see the brief for the original) —
 * collapsed into a single label since each status slot (Home's
 * `BottomPanel` and `OrderDetailsPage`, which must always show the same
 * status for the same order) has one status text rather than one status
 * text per address box.
 */
export function computeTrackingStatusLabel({
  isDropoffTracking,
  statusCode,
  remainingQueuePosition,
  dropoffStatus,
  fallbackLabel,
  language,
}: ComputeTrackingStatusLabelInput): string {
  if (!isDropoffTracking) return fallbackLabel;

  if (dropoffStatus === "done") return t("Delivered", language);

  if (statusCode === "Started" || statusCode === "Arrived") return t("PickingUpTheOrder", language);

  if (statusCode === "PickedUp") {
    if (remainingQueuePosition !== null && remainingQueuePosition > 0) {
      return tPlural("StopsLeftToDeliver", remainingQueuePosition, language);
    }
    if (remainingQueuePosition === 0) return t("Delivering", language);
  }

  return fallbackLabel;
}
